import {
  SELECT_INPUT_OTHER_VALUE,
  USE_CASE_OPTIONS
} from "@/src/common/business-layer/constants"
import Button from "@/src/sb/components/Button"
import { TextInput } from "@/src/sb/components/Input"
import classNames from "classnames"
import { useEffect, useState } from "react"

import CheckboxOption from "./CheckboxOption"
import Field from "./Field"
import { SPECIFY_INPUT_CLASSES } from "./fieldStyles"

export type UseCaseSelection = {
  useCases: Array<string>
  useCaseOther: string
}

export const EMPTY_USE_CASE_SELECTION: UseCaseSelection = {
  useCases: [],
  useCaseOther: ""
}

type UseCaseStepProps = {
  initialValues?: UseCaseSelection
  onSubmit: (values: UseCaseSelection) => void
  onBack: () => void
  onChange?: (values: UseCaseSelection) => void
  submitLabel?: string
  submitting?: boolean
  actionsAlign?: "between" | "end"
  error?: string
}

const UseCaseStep = ({
  initialValues = EMPTY_USE_CASE_SELECTION,
  onSubmit,
  onBack,
  onChange,
  submitLabel = "Next",
  submitting = false,
  actionsAlign = "between",
  error
}: UseCaseStepProps) => {
  const [useCases, setUseCases] = useState(initialValues.useCases)
  const [useCaseOther, setUseCaseOther] = useState(initialValues.useCaseOther)

  useEffect(() => {
    onChange?.({ useCases, useCaseOther })
  }, [useCases, useCaseOther])

  const otherSelected = useCases.includes(SELECT_INPUT_OTHER_VALUE)
  const canContinue =
    useCases.length > 0 &&
    (!otherSelected || useCaseOther.trim().length > 0) &&
    !submitting

  const toggle = (useCase: string) =>
    setUseCases((selected) =>
      selected.includes(useCase)
        ? selected.filter((value) => value !== useCase)
        : [...selected, useCase]
    )

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit({ useCases, useCaseOther })
      }}
    >
      {error ? (
        <p role="alert" className="text-xs text-[#F05042]">
          {error}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-x-3 sm:gap-y-2">
        {USE_CASE_OPTIONS.map(({ value, label }) => (
          <CheckboxOption
            key={value}
            label={label}
            checked={useCases.includes(value)}
            onChange={() => toggle(value)}
          />
        ))}
      </div>

      {otherSelected ? (
        <Field label="Please specify your use case" htmlFor="useCaseOther">
          <TextInput
            id="useCaseOther"
            name="useCaseOther"
            type="text"
            autoFocus
            placeholder="Enter your use case"
            value={useCaseOther}
            extraInputClasses={SPECIFY_INPUT_CLASSES}
            errorShiftCorrected={false}
            onChange={(e) => setUseCaseOther(e.target.value)}
          />
        </Field>
      ) : null}

      <div
        className={classNames("flex items-center", {
          "justify-between gap-3": actionsAlign === "between",
          "justify-end gap-2.5": actionsAlign === "end"
        })}
      >
        <Button
          type="primary"
          roundness="edge"
          htmlType="button"
          onClick={onBack}
          extraClassNames="dark:bg-[#1A1D21] dark:border-[#4B5563] dark:h-[38px]"
        >
          Back
        </Button>

        <Button
          type="primary"
          roundness="edge"
          htmlType="submit"
          loading={submitting}
          disabled={!canContinue}
          extraClassNames={classNames("dark:bg-[#4B5563] dark:h-[38px]", {
            "w-[112px]": actionsAlign === "between"
          })}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}

export default UseCaseStep
